import { useRef, useMemo, useEffect, ErrorInfo, useContext } from "react";
import { isEmpty, getNRErrorInfo } from "@swiggy-private/js-utils";
import { init, addRequestInterceptor, addResponseInterceptor } from "@swiggy-private/native-bridge";
import Blaze from "@swiggy-private/blaze.js";
import type { NativeRequest } from "@swiggy-private/native-bridge/dist/interfaces";
// eslint-disable-next-line import/named
import { ResponseContext } from "@swiggy-private/portal-dineout-context";
import { useRouter } from "next/compat/router";
import { initBridgeSdk } from "utils/bridgeSdk";
import { getPlatForm, isBrowserEnv } from "utils/browser";
import { getRestaurantId } from "utils/newrelic";
import { useEffectOnce } from "./useEffectOnce";
import packageJson from "../package.json";

// initalise Native Bridge SDK
export const Sdk = init();

const EMPTY_FUNCTION = () => void 0;

const getPrefix = (prefix?: string): string => (!isEmpty(prefix) ? `${prefix}_` : "");

const createNBRequestInterceptor = (recordNREvent?: Function, prefix?: string): VoidFunction =>
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    addRequestInterceptor((req: NativeRequest<{ state?: string; link?: string }>) => {
        if (recordNREvent == null) {
            return;
        }
        const projectPrefix = getPrefix(prefix);

        recordNREvent({
            eventType: `${projectPrefix}web_native_bridge_request`,
            id: req.requestId,
            type: req.type,
            subType: req.payload?.state ?? "",
            link: req.payload?.link ?? "",
        });
    });

const createNBResponseInterceptor = (recordNREvent?: Function, prefix?: string): VoidFunction =>
    addResponseInterceptor((res) => {
        if (recordNREvent == null) {
            return;
        }
        const projectPrefix = getPrefix(prefix);

        recordNREvent({
            eventType: `${projectPrefix}web_native_bridge_response`,
            id: res.requestId,
            type: res.type,
            statusCode: res.statusCode ?? -1,
            statusMessage: res.statusMessage ?? "",
            bridgeType: res.bridgeType ?? "",
        });
    });

export function useBootstrap(nbEventTypePrefix: string, isCached?: boolean): void {
    const initRef = useRef(false);
    const { state, dispatch } = useContext(ResponseContext);
    const router = useRouter();

    if (!initRef.current) {
        // init blaze
        Blaze.init({
            sendToAnalytics: EMPTY_FUNCTION,
            enableResourceTiming: false,
            nrConfig: {
                enable: true,
                config: {
                    NR_ENDPOINT: "https://insights-collector.newrelic.com/v1/accounts/737486/events", // enter newrelic endpoint here
                    NR_KEY: "e1687c0399396561695f01042f298f764ad2b997", // enter newrelic key here
                },
                meta: {
                    appName: nbEventTypePrefix, // enter app name
                    /* JS hash is generated without replacing with actual value
                    So cached file would be same for both canary vs non-canary, and would have unknown behavior */
                    appDeployName: isBrowserEnv()
                        ? (document?.querySelector("[data-app-name]") as HTMLDivElement)?.dataset.appName ?? ""
                        : "",
                    ...(isCached && { isHtmlCached: String(isCached) }),
                    appVersion: packageJson.version,
                    env: process.env.NEXT_PUBLIC_ENV || "production",
                    referer: isBrowserEnv() ? document?.referrer : "",
                    metaPlatform: getPlatForm(),
                    restaurantId: isBrowserEnv() ? getRestaurantId() : "--",
                },
            },
        });

        // create function to intialise NB
        initBridgeSdk({ state, dispatch, router, sdk: Sdk, override: true });
    }

    const requestInterceptor = useMemo(
        () => createNBRequestInterceptor(Blaze.recordCustomEvent, nbEventTypePrefix),
        [nbEventTypePrefix],
    );
    const responseInterceptor = useMemo(
        () => createNBResponseInterceptor(Blaze.recordCustomEvent, nbEventTypePrefix),
        [nbEventTypePrefix],
    );

    useEffectOnce(() => {
        initRef.current = true;
    });

    useEffect(() => {
        return (): void => {
            requestInterceptor();
            responseInterceptor();
        };
    }, [requestInterceptor, responseInterceptor]);
}

export const sendErrorBoundaryEvent = (
    error?: Error,
    errorInfo?: ErrorInfo,
    headers?: Record<string, string | undefined> | void,
) => {
    const errorBoundaryEvents = getNRErrorInfo(error, errorInfo);
    Blaze.recordCustomEvent({
        eventType: "webview_error_boundary",
        appName: "portal-dineout",
        ...errorBoundaryEvents,
        ...headers,
    });
};
